import type { ProductComparisonMatch } from '@fig/shared';
import { RefreshCw } from 'lucide-react';

import { ProductCard } from './ProductCard';

interface ProductCardGridProps {
  matches: ProductComparisonMatch[];
  maxDisplay?: number;
  onRefreshCard?: (index: number, currentMatch: ProductComparisonMatch) => void;
  refreshingIndex?: number | null;
}

export function ProductCardGrid({
  matches,
  maxDisplay = 3,
  onRefreshCard,
  refreshingIndex = null,
}: ProductCardGridProps) {
  const displayed = matches.slice(0, maxDisplay);
  const bestId = [...matches].sort((a, b) => b.recommendationScore - a.recommendationScore)[0]
    ?.productId;
  const emptySlots = Math.max(0, maxDisplay - displayed.length);

  return (
    <div>
      {/* Grid with equal-height cards */}
      <div
        className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 items-stretch"
        role="list"
      >
        {displayed.map((match, index) => (
          <div key={match.productId} role="listitem" className="relative flex flex-col">
            {onRefreshCard && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onRefreshCard(index, match);
                }}
                disabled={refreshingIndex !== null}
                className="absolute top-[18px] right-[18px] z-20 rounded-full p-1.5 bg-card border border-border shadow-sm hover:bg-muted transition-colors disabled:opacity-50"
                aria-label="Show next best option"
              >
                <RefreshCw
                  className={`h-3.5 w-3.5 text-muted-foreground ${refreshingIndex === index ? 'animate-spin' : ''}`}
                />
              </button>
            )}
            <ProductCard match={match} isBestMatch={match.productId === bestId} />
          </div>
        ))}

        {/* Empty placeholder slots */}
        {Array.from({ length: emptySlots }).map((_, i) => (
          <div
            key={`empty-${i}`}
            className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-border/50 bg-muted/10 p-8"
          >
            <span className="text-sm text-muted-foreground/60">Select product</span>
          </div>
        ))}
      </div>

      {/* Best Match tag — rendered below the grid, aligned under the best card */}
      {displayed.length > 0 && displayed.some((m) => m.productId === bestId) && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 -mt-1">
          {Array.from({ length: maxDisplay }).map((_, i) => {
            const match = displayed[i];
            if (match && match.productId === bestId) {
              return (
                <div key={match.productId}>
                  <div className="relative rounded-b-lg border border-t-0 border-border bg-card px-4 pb-2 pt-4 text-center">
                    <span className="text-sm font-semibold tracking-wide text-primary">
                      Best Match
                    </span>
                  </div>
                </div>
              );
            }
            return <div key={`bt-${i}`} />;
          })}
        </div>
      )}
    </div>
  );
}
